import Logo from "~/components/Logo"
import {
  articleBodyStyle,
  articleCompareImages,
  h1Style,
  h1Wrap,
  headerTextStyle,
  stepSectionStyle,
  unorderedList,
} from "./article.css"
import { MetaFunction } from "@remix-run/node"
import StickyHeader from "~/components/StickyHeader"
import { page } from "~/shared/styles.css"
import LinkButton from "~/components/LinkButton"
import Footer from "~/components/Footer"

export const meta: MetaFunction = () => {
  return [
    {
      title: "Contemporary Garden Ideas with AI",
    },
    {
      name: "description",
      content:
        "Discover how to create contemporary garden designs with AI. Rescape AI makes this process effortless.",
    },
    {
      property: "og:title",
      content: "Contemporary Garden Ideas with AI",
    },
    {
      property: "og:description",
      content:
        "Discover how to create contemporary garden designs with AI. Rescape AI makes this process effortless.",
    },
    {
      property: "og:image",
      content: "https://rescapeai.com/guide/garden-5-after-ai-contemporary-design.webp",
    },
    {
      tagName: "link",
      rel: "canonical",
      href: "https://rescapeai.com/garden-design-guide/contemporary/contemporary-garden-ideas-using-ai",
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "Contemporary Garden Ideas",
        description:
          "Discover how to create contemporary garden designs with AI. Rescape AI makes this process effortless.",
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2025-10-20",
        dateModified: "2025-10-20",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: "https://rescapeai.com/guide/garden-5-after-ai-contemporary-design.webp",
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id":
            "https://rescapeai.com/garden-design-guide/contemporary/contemporary-garden-ideas-using-ai",
        },
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/guide/garden-5-after-ai-contemporary-design.webp)",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>Garden Designs made using AI: Contemporary</h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />

      <div className={page}>
        <section className={stepSectionStyle}>
          <p className={articleBodyStyle}>
            In this guide, we’ll explore using Rescape AI for contemporary garden design.
            Rescape AI quickly helps generate concept visualisations helping you
            understand how you can design your garden ina way the you’ll love.
          </p>
          <p className={articleBodyStyle}>
            What makes AI particularly valuable for garden visualization is its ability to
            iterate quickly. Want to see how your patio would look with a Mediterranean
            theme versus a cottage garden style? Curious about whether that corner would
            work better with a water feature or a seating area? Rescape AI is a
            cost-effective tool to help you realise these design ideas.
          </p>
          <p className={articleBodyStyle}>
            So, let’s begin. I’ll take you through the feature options of contemporary
            garden design on Rescape AI with before and after photo’s processed by Rescape
            AI.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <div className={articleCompareImages}>
            <img
              src={"/guide/garden-1-before-ai-design.webp"}
              alt={"Garden before redesign"}
              loading="lazy"
            />
            <img
              src={"/guide/garden-1-after-ai-contemporary-design.webp"}
              alt={"Contemporary-design-by-ai"}
              loading="lazy"
            />
          </div>
          <p className={articleBodyStyle}>
            With little space to experiment with Rescape AI has provided simple
            suggestions to create a contemporary look in this garden.
          </p>
          <ul className={unorderedList}>
            <li>
              Rescape AI has recommended to replace the panel fencing with a slotted
              wooden fencing that climbing plants can grow against.
            </li>
            <li>
              A pathway has been added around the perimeter of the garden to make space
              for comfortable seating areas and potted planting.
            </li>
            <li>
              Keeping the cut short is important to provide a sleek finish in the garden.
            </li>
          </ul>
          <p className={articleBodyStyle}>
            The suggestions to the ai were Contemporary style and raised flower beds. This
            image only took one run to generate, other images in this series took several
            runs to generate the best outcome.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <div className={articleCompareImages}>
            <img
              src={"/guide/garden-2-before-ai-design.webp"}
              alt={"Garden before redesign"}
              loading="lazy"
            />
            <img
              src={"/guide/garden-2-after-ai-contemporary-design.webp"}
              alt={"Contemporary-design-by-ai"}
              loading="lazy"
            />
          </div>
          <p className={articleBodyStyle}>
            Here RescapeAI has made several alterations to turn this patio area into a
            comforting contemporary space. This image took two runs, the first choice came
            back with the perimeter changed but no seating area due to the lower
            creativity setting. Using the reload button on the bottom left of generated
            images, we re-ran it in the AI with a higher creativity setting and the same
            feature of “A relaxing seating area”
          </p>
          <ul className={unorderedList}>
            <li>
              Taking it back to the basics, Rescape AI has changed the patio to a smaller
              sized block and re-finished and painted the damaged wall as well as painting
              the wooden slatted fence a dark grey.
            </li>
            <li>
              A selection of potted plants are positioned behind the seating area to add a
              natural feel to the space.
            </li>
            <li>
              A comfortable light coloured wood furniture with light grey cushions is
              position to the side of the patio near the potted plants.
            </li>
          </ul>
        </section>
        <section className={stepSectionStyle}>
          <div className={articleCompareImages}>
            <img
              src={"/guide/garden-3-before-ai-design.webp"}
              alt={"Garden before redesign"}
              loading="lazy"
            />
            <img
              src={"/guide/garden-3-after-ai-contemporary-design.webp"}
              alt={"Contemporary-design-by-ai"}
              loading="lazy"
            />
          </div>
          <p className={articleBodyStyle}>
            While slightly changing the layout of the garden— the design principles can be
            applied to this garden. Replacing the large empty grass area with a circle
            patch of grass surrounded by light stained wooden terrace area is the defining
            feature of this space here. Further changes include.
          </p>
          <ul className={unorderedList}>
            <li>Reducing the selection of bushes to one neatly cut hedge.</li>
            <li>
              Some well cropped trees and shrubs create a naturalistic feel to the garden.
            </li>
            <li>
              Rescape AI recommends to pick some square and rectangle seating to to go
              with a wooden bench at the edges of the circle grass area.
            </li>
            <li>
              Plants fill the space, but the conforming to a reduced colour scheme the
              outcome is pleasing.
            </li>
          </ul>
        </section>
        <section className={stepSectionStyle}>
          <div className={articleCompareImages}>
            <img
              src={"/guide/garden-4-before-ai-design.webp"}
              alt={"Garden before redesign"}
              loading="lazy"
            />
            <img
              src={"/guide/garden-4-after-ai-contemporary-design.webp"}
              alt={"Contemporary-design-by-ai"}
              loading="lazy"
            />
          </div>
          <p className={articleBodyStyle}>
            Even with the large tree dominating this image Rescape AI can still generate a
            good image output. Here we masked the tree as it is a feature we want to keep
            in the image.
          </p>
          <ul className={unorderedList}>
            <li>
              A comfortable seating area is situated underneath the focal point tree.
            </li>
            <li>
              The grass is tidied up and another seating area is created at the bottom of
              the garden.
            </li>
            <li>
              The red brick on the wall is replaced with higher wooden slatted panels for
              privacy and contemporary design conformity.
            </li>
            <li>
              Finally wooden panels are placed across the garden allowing climbing plants
              to drape across them.
            </li>
          </ul>
        </section>
        <section className={stepSectionStyle}>
          <div className={articleCompareImages}>
            <img
              src={"/guide/garden-5-before-ai-design.webp"}
              alt={"Garden before redesign"}
              loading="lazy"
            />
            <img
              src={"/guide/garden-5-after-ai-contemporary-design.webp"}
              alt={"Contemporary-design-by-ai"}
              loading="lazy"
            />
            <img
              src={"/guide/garden-5.2-after-ai-contemporary-design.webp"}
              alt={"Contemporary-design-by-ai"}
              style={{
                gridColumnStart: 1,
                gridColumnEnd: 3,
                height: "200px",
                objectFit: "cover",
              }}
              loading="lazy"
            />
          </div>
          <p className={articleBodyStyle}>
            Here the prompt selections were “contemporary & wooden terrace”. We masked out
            the houses and perimeter to give Rescape AI some direction. Rescape AI has
            recommended a large landscaping project with wonderful outcomes.
          </p>
          <ul className={unorderedList}>
            <li>
              The sloped bottom left of the garden is leveled out to match the height of
              the bottom left.
            </li>
            <li>
              A large luxurious wooden terrace is added up by the back door of the house.
            </li>
            <li>In both images, steps help reduce the difference in elevation.</li>
          </ul>
        </section>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <LinkButton url="/hello/signup">Get Started here</LinkButton>
        </div>
        <Footer showLinks={false} />
      </div>
    </>
  )
}

export default Index
